import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { partnerQueryKeys } from "../../api/queryKeys";
import { fetchPartnerDocumentRequest } from "../../api/endpoints/documents";
import { EmptyState, StatusChip } from "../../components/ui";
import { requestStage } from "./documentHelpers";
import DocumentRequestWorkspace from "./DocumentRequestWorkspace";

const DocumentRequestDetailPage = () => {
  const { requestId } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const {
    data: request,
    isLoading,
    isError,
    refetch,
  } = useQuery(
    partnerQueryKeys.documentRequest(requestId),
    () => fetchPartnerDocumentRequest(requestId),
    { refetchOnMount: true }
  );

  const refresh = () => {
    refetch();
    queryClient.invalidateQueries(partnerQueryKeys.documentRequests());
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-24">
        <CircularProgress />
      </div>
    );
  }

  if (isError || !request) {
    return (
      <EmptyState
        title="Couldn't load this request"
        description="It may have been revoked."
        action={
          <Link to="/partner/documents" className="text-sm font-semibold text-teal-700">
            Back to documents
          </Link>
        }
      />
    );
  }

  const stage = requestStage(request);

  return (
    <div>
      <div className="mb-5">
        <span
          onClick={() => navigate(-1)}
          className="text-xs font-semibold text-teal-700 no-underline hover:underline cursor-pointer"
        >
          ← All document requests
        </span>
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          <h2 className="m-0 text-xl font-bold text-slate-900">{request.title}</h2>
          <StatusChip tone={stage.tone}>{stage.label}</StatusChip>
          {request.parentRequestId ? <StatusChip tone="rose">Reupload</StatusChip> : null}
        </div>
        <p className="m-0 mt-0.5 text-sm text-slate-500">
          {request.clientName || "Client"}
          {request.targetModuleType ? ` · files into ${request.targetModuleType}` : ""}
        </p>
      </div>
      <DocumentRequestWorkspace request={request} onChanged={refresh} />
    </div>
  );
};

export default DocumentRequestDetailPage;
